import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, addDoc, doc, deleteDoc, query, orderBy
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Divider, Avatar, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Tab, Tabs, LinearProgress
} from '@mui/material';
import { deptCode } from '../utils/depts';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import { enrichCompany, mapCompanyToForm } from '../services/enrichLayer';
import { sparaKundlogga } from '../modules/crm/bildkopia';
import NyKundDialog from '../modules/crm/NyKundDialog';
import { segmentColor, SEGMENT_OPTIONS, ORGANISATIONSTYP_OPTIONS } from '../modules/crm/kundformular';
import { SegmentEtikett } from '../modules/crm/KategoriVal';
import KategoriseraKunderDialog, { saknarKategori } from '../modules/crm/KategoriseraKunderDialog';
import CategoryIcon from '@mui/icons-material/Category';

const INDUSTRIES = [
  'Fastighet', 'Bygg & Entreprenad', 'Infrastruktur', 'Industri',
  'Offentlig sektor', 'Energi', 'IT & Tech', 'Teknikkonsult', 'Övrigt'
];

const COMPANY_STATUSES = [
  { key: 'prospect',  label: 'Prospect',   color: '#1565C0', bg: '#E3F2FD' },
  { key: 'aktiv',     label: 'Aktiv kund', color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'vilande',   label: 'Vilande',    color: '#795548', bg: '#EFEBE9' },
];

function initials(name) {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function statusInfo(key) {
  return COMPANY_STATUSES.find(s => s.key === key) || null;
}

export default function CRMCompanies() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRelation, setFilterRelation] = useState('');
  const [filterSegment, setFilterSegment] = useState(''); // '' | segment | '__saknas'
  const [filterOrgTyp, setFilterOrgTyp] = useState('');   // '' | organisationstyp | '__saknas'
  const [filterSaknarOrgNr, setFilterSaknarOrgNr] = useState(false);
  const [kategoriseraOpen, setKategoriseraOpen] = useState(false);
  // Säkerhetsflaggor (sätts från kundprojekt eller manuellt på kundkortet).
  const [filterErfarenhetSkydd, setFilterErfarenhetSkydd] = useState(false);
  const [filterIsoleradMiljo, setFilterIsoleradMiljo] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  function handleSort(col) {
    if (sortBy === col) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortBy(''); setSortDir('asc'); }
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { if (searchParams.get('new') === '1') setDialog(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true);
    const [compSnap, deptSnap] = await Promise.all([
      getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
      getDocs(collection(db, 'departments')),
    ]);
    setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleDelete(id, e) {
    e.stopPropagation();
    if (!window.confirm('Ta bort företaget? Kontakter raderas också.')) return;
    const contactsSnap = await getDocs(collection(db, 'crmCompanies', id, 'contacts'));
    await Promise.all(contactsSnap.docs.map(d => deleteDoc(d.ref)));
    await deleteDoc(doc(db, 'crmCompanies', id));
    setCompanies(prev => prev.filter(c => c.id !== id));
  }

  const filtered = companies.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name?.toLowerCase().includes(q) ||
      c.city?.toLowerCase().includes(q) || c.industry?.toLowerCase().includes(q) ||
      c.orgNumber?.toLowerCase().includes(q);
    const matchDept = !filterDept || (c.departmentIds || []).includes(filterDept);
    const matchInd = !filterIndustry || c.industry === filterIndustry;
    const matchStatus = !filterStatus || c.status === filterStatus;
    const matchRelation =
      !filterRelation ? true :
      filterRelation === 'aterforsaljare' ? c.isReseller :
      filterRelation === 'leverantor'     ? c.isSupplier :
      filterRelation === 'kund'           ? (!c.isReseller && !c.isSupplier) :
      true;
    const matchSegment = !filterSegment
      || (filterSegment === '__saknas' ? !SEGMENT_OPTIONS.includes(c.segment) : c.segment === filterSegment);
    const matchOrgTyp = !filterOrgTyp
      || (filterOrgTyp === '__saknas' ? !ORGANISATIONSTYP_OPTIONS.includes(c.organisationstyp) : c.organisationstyp === filterOrgTyp);
    const matchOrgNr = !filterSaknarOrgNr || !c.orgNumber;
    const matchSakerhet = (!filterErfarenhetSkydd || c.erfarenhetSakerhetsskydd === 'ja')
      && (!filterIsoleradMiljo || c.harIsoleradMiljo === 'ja');
    return matchSearch && matchDept && matchInd && matchStatus && matchRelation && matchSegment && matchOrgTyp
      && matchOrgNr && matchSakerhet;
  });

  const sorted = sortBy ? [...filtered].sort((a, b) => {
    let va = '', vb = '';
    if (sortBy === 'name')      { va = a.name || ''; vb = b.name || ''; }
    if (sortBy === 'status')    { va = a.status || ''; vb = b.status || ''; }
    if (sortBy === 'industry')  { va = a.industry || ''; vb = b.industry || ''; }
    if (sortBy === 'city')      { va = a.city || ''; vb = b.city || ''; }
    if (sortBy === 'orgNumber') { va = a.orgNumber || ''; vb = b.orgNumber || ''; }
    if (sortBy === 'segment')   { va = a.segment  || ''; vb = b.segment  || ''; }
    if (sortBy === 'organisationstyp') { va = a.organisationstyp || ''; vb = b.organisationstyp || ''; }
    if (sortBy === 'employees') {
      va = parseInt(a.employees) || 0;
      vb = parseInt(b.employees) || 0;
      return sortDir === 'asc' ? va - vb : vb - va;
    }
    return sortDir === 'asc'
      ? va.localeCompare(vb, 'sv')
      : vb.localeCompare(va, 'sv');
  }) : filtered;

  const counts = COMPANY_STATUSES.reduce((acc, s) => {
    acc[s.key] = companies.filter(c => c.status === s.key).length;
    return acc;
  }, {});

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Top bar */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Affärer – Kunder</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {filtered.length} av {companies.length} företag
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined"
            onClick={() => navigate('/crm/leads')}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
            Leads
          </Button>
          <Button variant="outlined" startIcon={<CategoryIcon />}
            onClick={() => setKategoriseraOpen(true)}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
            Kategorisera kunder ({companies.filter(saknarKategori).length})
          </Button>
          <Button variant="outlined" startIcon={<TableChartIcon />}
            onClick={() => setBulkDialog(true)}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
            Bulk-import
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => setDialog(true)}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            Nytt företag
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Status filter chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={`Alla (${companies.length})`}
            onClick={() => setFilterStatus('')}
            size="small"
            variant={filterStatus === '' ? 'filled' : 'outlined'}
            sx={{ fontWeight: filterStatus === '' ? 700 : 400,
              bgcolor: filterStatus === '' ? '#1a1a1a' : 'transparent',
              color: filterStatus === '' ? 'white' : 'text.primary' }}
          />
          {COMPANY_STATUSES.map(s => (
            <Chip key={s.key} size="small"
              label={`${s.label} (${counts[s.key] || 0})`}
              onClick={() => setFilterStatus(filterStatus === s.key ? '' : s.key)}
              variant={filterStatus === s.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: filterStatus === s.key ? 700 : 400,
                bgcolor: filterStatus === s.key ? s.color : 'transparent',
                color: filterStatus === s.key ? 'white' : s.color,
                borderColor: s.color }}
            />
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {[
            { key: 'kund',           label: 'Kund',           color: '#1565C0', count: companies.filter(c => !c.isReseller && !c.isSupplier).length },
            { key: 'aterforsaljare', label: 'Återförsäljare', color: '#7B1FA2', count: companies.filter(c => c.isReseller).length },
            { key: 'leverantor',     label: 'Leverantör',     color: '#1B5E20', count: companies.filter(c => c.isSupplier).length },
          ].map(({ key, label, color, count }) => (
            <Chip key={key} size="small"
              label={`${label} (${count})`}
              onClick={() => setFilterRelation(v => v === key ? '' : key)}
              variant={filterRelation === key ? 'filled' : 'outlined'}
              sx={{ fontWeight: filterRelation === key ? 700 : 400,
                bgcolor: filterRelation === key ? color : 'transparent',
                color: filterRelation === key ? 'white' : color,
                borderColor: color }}
            />
          ))}
        </Box>

        {/* Filter row */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Sök namn, ort, bransch, org.nr..."
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240, bgcolor: 'white', borderRadius: 1 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )}} />
          <TextField select size="small" value={filterIndustry}
            onChange={e => setFilterIndustry(e.target.value)}
            sx={{ minWidth: 180, bgcolor: 'white', borderRadius: 1 }} label="Bransch">
            <MenuItem value="">Alla branscher</MenuItem>
            {INDUSTRIES.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={filterDept}
            onChange={e => setFilterDept(e.target.value)}
            sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }} label="Affärsområde">
            <MenuItem value="">Alla affärsområden</MenuItem>
            {departments.map(d => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" value={filterSegment}
            onChange={e => setFilterSegment(e.target.value)}
            sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }} label="Segment">
            <MenuItem value="">Alla segment</MenuItem>
            <MenuItem value="__saknas"><i>Saknar segment</i></MenuItem>
            {SEGMENT_OPTIONS.map(s => (
              <MenuItem key={s} value={s}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: segmentColor(s), flexShrink: 0 }} />
                  {s}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" value={filterOrgTyp}
            onChange={e => setFilterOrgTyp(e.target.value)}
            sx={{ minWidth: 190, bgcolor: 'white', borderRadius: 1 }} label="Organisationstyp">
            <MenuItem value="">Alla organisationstyper</MenuItem>
            <MenuItem value="__saknas"><i>Saknar organisationstyp</i></MenuItem>
            {ORGANISATIONSTYP_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <Chip label={`Saknar org.nr (${companies.filter(c => !c.orgNumber).length})`} clickable
            onClick={() => setFilterSaknarOrgNr(v => !v)} aria-pressed={filterSaknarOrgNr}
            variant={filterSaknarOrgNr ? 'filled' : 'outlined'}
            sx={filterSaknarOrgNr
              ? { bgcolor: '#FFF8E1', color: '#E65100', fontWeight: 700, borderColor: '#E65100' }
              : { bgcolor: 'white' }} />
          <Chip icon={<ShieldOutlinedIcon />} label="Erfarenhet av säkerhetsskydd" clickable
            onClick={() => setFilterErfarenhetSkydd(v => !v)} aria-pressed={filterErfarenhetSkydd}
            variant={filterErfarenhetSkydd ? 'filled' : 'outlined'}
            sx={filterErfarenhetSkydd
              ? { bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700, borderColor: '#E65100', '& .MuiChip-icon': { color: '#E65100' } }
              : { bgcolor: 'white' }} />
          <Chip icon={<CloudOffOutlinedIcon />} label="Har isolerad miljö" clickable
            onClick={() => setFilterIsoleradMiljo(v => !v)} aria-pressed={filterIsoleradMiljo}
            variant={filterIsoleradMiljo ? 'filled' : 'outlined'}
            sx={filterIsoleradMiljo
              ? { bgcolor: '#F3E5F5', color: '#6A1B9A', fontWeight: 700, borderColor: '#6A1B9A', '& .MuiChip-icon': { color: '#6A1B9A' } }
              : { bgcolor: 'white' }} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {companies.length === 0
                ? 'Inga företag ännu. Lägg till ditt första.'
                : 'Inga träffar på sökning/filter.'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  {[
                    { col: 'name',      label: 'Företag' },
                    { col: 'status',    label: 'Status' },
                    { col: 'segment',   label: 'Segment' },
                    { col: 'organisationstyp', label: 'Organisationstyp' },
                    { col: 'industry',  label: 'Bransch' },
                    { col: 'city',      label: 'Ort' },
                    { col: 'employees', label: 'Storlek' },
                  ].map(({ col, label }) => (
                    <TableCell key={col} sx={{ py: 1.5 }}>
                      <TableSortLabel
                        active={sortBy === col}
                        direction={sortBy === col ? sortDir : 'asc'}
                        onClick={() => handleSort(col)}
                        sx={{
                          fontWeight: 700, fontSize: '0.72rem',
                          textTransform: 'uppercase', letterSpacing: 0.5, color: '#666',
                          '&.Mui-active': { color: '#1a1a1a' },
                          '& .MuiTableSortLabel-icon': { fontSize: 14 },
                        }}>
                        {label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  {['Affärsområde'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem',
                      textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                  <TableCell sx={{ width: 80 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map(company => {
                  const st = statusInfo(company.status);
                  return (
                    <TableRow key={company.id}
                      hover
                      onClick={() => navigate(`/crm/companies/${company.id}`)}
                      sx={{ cursor: 'pointer',
                        borderLeft: st ? `3px solid ${st.color}` : '3px solid transparent',
                        '&:hover': { bgcolor: '#fafafa' } }}>

                      {/* Företag */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={company.logoUrl || undefined}
                            sx={{ bgcolor: '#1a1a1a', width: 34, height: 34,
                              fontSize: '0.7rem', flexShrink: 0 }}>
                            {!company.logoUrl && initials(company.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="600" sx={{ lineHeight: 1.3 }}>
                              {company.name}
                            </Typography>
                            {company.phone && (
                              <Typography variant="caption" color="text.disabled">
                                {company.phone}
                              </Typography>
                            )}
                            {!company.orgNumber && (
                              <Typography variant="caption" sx={{ color: '#E65100', display: 'block', lineHeight: 1.2 }}>
                                Saknar org.nr
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {st && (
                            <Chip label={st.label} size="small"
                              sx={{ fontSize: '0.65rem', height: 20,
                                bgcolor: st.bg, color: st.color, fontWeight: 700 }} />
                          )}
                          {company.isReseller && (
                            <Chip label="ÅF" size="small"
                              sx={{ fontSize: '0.65rem', height: 20,
                                bgcolor: '#7B1FA2', color: 'white', fontWeight: 700 }} />
                          )}
                          {company.isSupplier && (
                            <Chip label="LV" size="small"
                              sx={{ fontSize: '0.65rem', height: 20,
                                bgcolor: '#1B5E20', color: 'white', fontWeight: 700 }} />
                          )}
                        </Box>
                      </TableCell>

                      {/* Segment */}
                      <TableCell>
                        {company.segment ? <SegmentEtikett segment={company.segment} />
                          : <Typography variant="body2" color="text.disabled">–</Typography>}
                      </TableCell>

                      {/* Organisationstyp */}
                      <TableCell>
                        <Typography variant="body2" color={company.organisationstyp ? 'text.secondary' : 'text.disabled'}>
                          {company.organisationstyp || '–'}
                        </Typography>
                      </TableCell>

                      {/* Bransch */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {company.industry || '–'}
                        </Typography>
                      </TableCell>

                      {/* Ort */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {company.city || '–'}
                        </Typography>
                      </TableCell>

                      {/* Storlek */}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {company.employees ? `${company.employees} anst.` : ''}
                          {company.employees && company.revenue ? ' · ' : ''}
                          {company.revenue || ''}
                        </Typography>
                      </TableCell>

                      {/* Affärsområden */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                          {(company.departmentIds || []).map(dId => {
                            const dept = departments.find(d => d.id === dId);
                            return dept ? (
                              <Tooltip key={dId} title={dept.name}>
                                <Chip label={deptCode(dept)} size="small"
                                  sx={{ fontSize: '0.62rem', height: 18,
                                    bgcolor: '#1a1a1a', color: 'white' }} />
                              </Tooltip>
                            ) : null;
                          })}
                        </Box>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {company.website && (
                            <Tooltip title="Öppna webbplats">
                              <IconButton size="small"
                                onClick={e => {
                                  e.stopPropagation();
                                  window.open(
                                    company.website.startsWith('http')
                                      ? company.website
                                      : 'https://' + company.website,
                                    '_blank'
                                  );
                                }}
                                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Ta bort">
                            <IconButton size="small"
                              onClick={e => handleDelete(company.id, e)}
                              sx={{ opacity: 0.3, '&:hover': { opacity: 1, color: 'error.main' } }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Ny kund – samma dialog som på Affärer – Pipeline (med AI-tolkning av mailsignaturer) */}
      {dialog && (
        <NyKundDialog open companies={companies} departments={departments} uid={currentUser.uid}
          onClose={() => setDialog(false)} onSparad={() => load()} />
      )}

      {/* Bulk-import dialog */}
      <KategoriseraKunderDialog open={kategoriseraOpen} onClose={() => setKategoriseraOpen(false)}
        companies={companies}
        onSaved={(id, falt) => setCompanies(prev => prev.map(c => (c.id === id ? { ...c, ...falt } : c)))} />

      <BulkImportDialog
        open={bulkDialog}
        onClose={() => setBulkDialog(false)}
        industries={INDUSTRIES}
        onImported={(rows) => {
          setBulkDialog(false);
          setCompanies(prev => {
            const next = [...prev, ...rows];
            return next.sort((a, b) => a.name.localeCompare(b.name));
          });
        }}
      />
    </Box>
  );
}

// ── Bulk-import ──────────────────────────────────────────────────────────────
const BULK_COLS = [
  { key: 'name',        label: 'Företagsnamn *', width: 220 },
  { key: 'orgNumber',   label: 'Org.nr',          width: 130 },
  { key: 'industry',    label: 'Bransch',         width: 160 },
  { key: 'city',        label: 'Ort',             width: 120 },
  { key: 'revenue',     label: 'Omsättning',      width: 120 },
  { key: 'website',     label: 'Webbplats',       width: 180 },
  { key: 'linkedinUrl', label: 'LinkedIn-URL',    width: 220 },
];
const BULK_EMPTY_ROW = () => ({ name:'', orgNumber:'', industry:'', city:'', revenue:'', website:'', linkedinUrl:'' });

function parsePaste(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (!lines.length) return [];
  const rows = lines.map(l => l.split('\t').map(c => c.trim()));
  // Om första raden ser ut som rubriker (ingen siffra i namn, etc) skippa den
  const firstIsHeader = rows[0].every(c => /^[a-zåäöA-ZÅÄÖ\s/&.]+$/.test(c) && !c.match(/^\d+$/));
  const dataRows = firstIsHeader && rows.length > 1 ? rows.slice(1) : rows;
  const headers = firstIsHeader ? rows[0].map(h => h.toLowerCase()) : null;

  const colMap = headers ? headers.map(h => {
    if (/namn|name|företag|company/.test(h))    return 'name';
    if (/org|nummer|nr|number/.test(h))          return 'orgNumber';
    if (/bransch|branch|industri|industry/.test(h)) return 'industry';
    if (/ort|stad|city/.test(h))                 return 'city';
    if (/omsättn|revenue|intäkt/.test(h))        return 'revenue';
    if (/webb|web|site|url/.test(h) && !/linkedin/.test(h)) return 'website';
    if (/linkedin/.test(h))                      return 'linkedinUrl';
    return null;
  }) : null;

  return dataRows.map(cells => {
    const row = BULK_EMPTY_ROW();
    if (colMap) {
      colMap.forEach((field, i) => { if (field && cells[i]) row[field] = cells[i]; });
    } else {
      // Inga rubriker: första kolumn = namn, andra = org.nr osv
      if (cells[0]) row.name      = cells[0];
      if (cells[1]) row.orgNumber = cells[1];
      if (cells[2]) row.industry  = cells[2];
      if (cells[3]) row.city      = cells[3];
      if (cells[4]) row.revenue   = cells[4];
      if (cells[5]) row.website   = cells[5];
    }
    return row;
  }).filter(r => r.name);
}

function BulkImportDialog({ open, onClose, industries, onImported }) {
  const [tab, setTab]           = useState(0); // 0=klistra, 1=fil
  const [pasteText, setPasteText] = useState('');
  const [rows, setRows]         = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [colMap, setColMap]     = useState({}); // för fil-fliken
  const [fileHeaders, setFileHeaders] = useState([]);
  const [fileRows, setFileRows] = useState([]);
  const fileRef = useRef();

  function resetAll() {
    setPasteText(''); setRows([]); setFileHeaders([]); setFileRows([]); setColMap({}); setProgress(0);
  }

  function handleParsePaste() {
    setRows(parsePaste(pasteText).map((r, i) => ({ ...r, _id: i })));
  }

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const wb = XLSX.read(ev.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
      if (!data.length) return;
      const headers = data[0].map(String);
      setFileHeaders(headers);
      setFileRows(data.slice(1).filter(r => r.some(Boolean)));
      // Auto-mappa rubriker
      const auto = {};
      headers.forEach((h, i) => {
        const lh = h.toLowerCase();
        if (/namn|name|företag/.test(lh))    auto[i] = 'name';
        else if (/org|nummer/.test(lh))       auto[i] = 'orgNumber';
        else if (/bransch|industri/.test(lh)) auto[i] = 'industry';
        else if (/ort|stad/.test(lh))         auto[i] = 'city';
        else if (/omsättn|revenue/.test(lh))  auto[i] = 'revenue';
        else if (/webb|site|url/.test(lh))    auto[i] = 'website';
      });
      setColMap(auto);
      // Bygg preview-rader
      const mapped = data.slice(1).filter(r => r.some(Boolean)).map((r, i) => {
        const row = BULK_EMPTY_ROW();
        Object.entries(auto).forEach(([ci, field]) => { if (r[ci]) row[field] = String(r[ci]); });
        return { ...row, _id: i };
      }).filter(r => r.name);
      setRows(mapped);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }

  function applyFileMapping() {
    const mapped = fileRows.map((r, i) => {
      const row = BULK_EMPTY_ROW();
      Object.entries(colMap).forEach(([ci, field]) => { if (field && r[ci] != null) row[field] = String(r[ci]); });
      return { ...row, _id: i };
    }).filter(r => r.name);
    setRows(mapped);
  }

  async function handleImport() {
    const valid = rows.filter(r => r.name?.trim());
    if (!valid.length) return;
    setImporting(true);
    const saved = [];
    const total = valid.length;
    for (let i = 0; i < total; i++) {
      const { _id, ...data } = valid[i];
      // Auto-enrich from LinkedIn if URL present
      let enriched = {};
      if (data.linkedinUrl) {
        try {
          const liData = await enrichCompany(data.linkedinUrl);
          const mapped = mapCompanyToForm(liData);
          enriched = {
            website:     mapped.website     || data.website,
            description: mapped.description,
            employees:   mapped.employees   || data.employees,
            foundedYear: mapped.foundedYear,
            companyType: mapped.companyType,
            city:        mapped.city        || data.city,
            address:     mapped.address     || data.address,
            specialties: mapped.specialties,
          };
        } catch (_) { /* skip enrichment on error, still save row */ }
      }
      const ref = await addDoc(collection(db, 'crmCompanies'), {
        ...data, ...enriched,
        name: data.name.trim(), createdAt: new Date(), status: 'prospect',
      });
      sparaKundlogga(ref.id, { ...data, ...enriched }); // i bakgrunden
      saved.push({ id: ref.id, ...data, ...enriched });
      setProgress(Math.round(((i + 1) / total) * 100));
    }
    setImporting(false);
    onImported(saved);
    resetAll();
  }

  const OUR_FIELDS = [
    { key: '',            label: '— Ignorera —' },
    { key: 'name',        label: 'Företagsnamn' },
    { key: 'orgNumber',   label: 'Org.nr' },
    { key: 'industry',    label: 'Bransch' },
    { key: 'city',        label: 'Ort' },
    { key: 'revenue',     label: 'Omsättning' },
    { key: 'website',     label: 'Webbplats' },
    { key: 'linkedinUrl', label: 'LinkedIn-URL' },
  ];

  return (
    <Dialog open={open} onClose={() => { onClose(); resetAll(); }} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '85vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TableChartIcon sx={{ color: '#FF6B00', fontSize: 26 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">Bulk-import av kunder</Typography>
            <Typography variant="caption" color="text.secondary">Klistra in från Excel eller ladda upp fil — granska och redigera innan import</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setRows([]); }}
        sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Tab label="Klistra in från Excel" />
        <Tab label="Ladda upp fil (.xlsx / .csv)" />
      </Tabs>

      <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>

        {/* Tab 0 – Paste */}
        {tab === 0 && rows.length === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Kopiera celler direkt från Excel (Ctrl+C) och klistra in nedan. Rad 1 kan vara rubriker — de mappas automatiskt mot: <b>Företagsnamn, Org.nr, Bransch, Ort, Omsättning, Webbplats</b>.
            </Typography>
            <TextField fullWidth multiline rows={8} size="small"
              placeholder={"Företagsnamn\tOrg.nr\tBransch\nSkanska AB\t556000-0000\tBygg & Entreprenad\nNCC Group\t556034-5174\tBygg & Entreprenad"}
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }} />
            <Button variant="contained" disabled={!pasteText.trim()} onClick={handleParsePaste}
              sx={{ alignSelf: 'flex-start', bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
              Tolka innehåll →
            </Button>
          </Box>
        )}

        {/* Tab 1 – File */}
        {tab === 1 && rows.length === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFileUpload} />
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}
              sx={{ alignSelf: 'flex-start' }}>
              Välj fil (.xlsx, .xls, .csv)
            </Button>
            {fileHeaders.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Mappa kolumner från filen → våra fält
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {fileHeaders.map((h, i) => (
                    <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>{h}</Typography>
                      <TextField select size="small" value={colMap[i] || ''}
                        onChange={e => setColMap(m => ({ ...m, [i]: e.target.value }))}>
                        {OUR_FIELDS.map(f => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
                      </TextField>
                    </Box>
                  ))}
                </Box>
                <Button variant="contained" sx={{ mt: 2, bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}
                  onClick={applyFileMapping}>
                  Förhandsgranska →
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Preview + redigerbar tabell */}
        {rows.length > 0 && (
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={600}>
                {rows.filter(r => r.name?.trim()).length} företag redo att importeras — redigera direkt i tabellen
              </Typography>
              <Button size="small" onClick={() => setRows(r => [...r, { ...BULK_EMPTY_ROW(), _id: Date.now() }])}>
                + Lägg till rad
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto', borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {BULK_COLS.map(c => (
                      <TableCell key={c.key} sx={{ fontWeight: 700, fontSize: '0.75rem',
                        bgcolor: 'background.paper', minWidth: c.width }}>
                        {c.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ bgcolor: 'background.paper', width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, ri) => (
                    <TableRow key={row._id} hover>
                      {BULK_COLS.map(col => (
                        <TableCell key={col.key} sx={{ p: '4px 6px' }}>
                          {col.key === 'industry' ? (
                            <TextField select size="small" fullWidth variant="standard"
                              value={row[col.key] || ''}
                              onChange={e => setRows(rs => rs.map((r, i) => i === ri ? { ...r, [col.key]: e.target.value } : r))}
                              SelectProps={{ disableUnderline: true }}
                              sx={{ '& .MuiInput-root': { fontSize: '0.8rem' } }}>
                              <MenuItem value="">—</MenuItem>
                              {industries.map(ind => <MenuItem key={ind} value={ind}>{ind}</MenuItem>)}
                            </TextField>
                          ) : (
                            <TextField size="small" fullWidth variant="standard"
                              value={row[col.key] || ''}
                              onChange={e => setRows(rs => rs.map((r, i) => i === ri ? { ...r, [col.key]: e.target.value } : r))}
                              InputProps={{ disableUnderline: true, sx: { fontSize: '0.8rem' } }}
                              error={col.key === 'name' && !row.name?.trim()} />
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={{ p: '2px' }}>
                        <IconButton size="small" onClick={() => setRows(rs => rs.filter((_, i) => i !== ri))}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {importing && <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />}
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        {rows.length > 0 && (
          <Button size="small" onClick={() => setRows([])} sx={{ mr: 'auto' }}>
            ← Ändra indata
          </Button>
        )}
        <Button onClick={() => { onClose(); resetAll(); }}>Avbryt</Button>
        {rows.length > 0 && (
          <Button variant="contained" onClick={handleImport}
            disabled={importing || !rows.some(r => r.name?.trim())}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {importing ? `Importerar… ${progress}%` : `Importera ${rows.filter(r => r.name?.trim()).length} företag`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
